import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Save, X } from 'lucide-react';
import { EditCostBudgetForm } from './edit-cost-budget-form';
import { EditSalesBudgetForm } from './edit-sales-budget-form';
import { EditSalesBudgetFormTM } from './edit-sales-budget-form-tm';
import { EditResourceBudgetForm } from './edit-resource-budget-form';
import { BudgetRevisionPanel } from './budget-revision-panel';
import type { BudgetData, ProjectType, CostCategory, SalesCategory, BillingTriggerType } from '@/types/budget.types';
import { fetchDefaultCategoriesAsCostCategories, fetchDefaultCategoriesAsSalesCategories } from '@/lib/data-service';



interface EditBudgetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgetData: BudgetData;
  projectType: ProjectType;
  onSave: (updatedBudget: Partial<BudgetData>, revisionReason: string, saveAsBaseline: boolean) => Promise<void>;
  initialTab?: 'cost' | 'sales' | 'resource';
  projectStartDate?: string;
  projectEndDate?: string;
  /** Project-level billing trigger type — passed through to EditSalesBudgetForm (Phase 8+). */
  billingTriggerType?: BillingTriggerType;
}

export function EditBudgetModal({
  open,
  onOpenChange,
  budgetData,
  projectType,
  onSave,
  initialTab = 'cost',
  projectStartDate,
  projectEndDate,
  billingTriggerType = 'date',
}: EditBudgetModalProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Track changes for each budget type
  const [costBudgetChanges, setCostBudgetChanges] = useState<any>(null);
  const [salesBudgetChanges, setSalesBudgetChanges] = useState<any>(null);
  const [resourceBudgetChanges, setResourceBudgetChanges] = useState<any>(null);
  
  // Revision details
  const [revisionReason, setRevisionReason] = useState('');
  const [saveAsBaseline, setSaveAsBaseline] = useState(false);
  const [reasonCategory, setReasonCategory] = useState<import('@/types/budget.types').RevisionReasonCategory | ''>('');
  const [baselineName, setBaselineName] = useState('');

  // Fetch default GlobalCategories to auto-populate when no cost budget categories exist
  const [defaultGlobalCategories, setDefaultGlobalCategories] = useState<CostCategory[]>([]);
  
  useEffect(() => {
    if (open && budgetData.costBudget?.categories?.length === 0) {
      fetchDefaultCategoriesAsCostCategories()
        .then(categories => {
          // Pre-populate Labour category budget from resource budget cascade values
          const roles = budgetData.resourceBudget?.byRole ?? [];
          if (roles.length > 0) {
            const labourTotal = roles.reduce((sum, role) => sum + (role.budgetCost ?? 0), 0);
            if (labourTotal > 0) {
              categories = categories.map(cat =>
                cat.type === 'labour'
                  ? { ...cat, budget: labourTotal, available: labourTotal }
                  : cat
              );
            }
          }

          setDefaultGlobalCategories(categories);
        })
        .catch(err => {
          console.warn('[EditBudgetModal] Failed to load default GlobalCategories:', err);
        });
    }
  }, [open, budgetData.costBudget?.categories?.length]);

  // Fetch default GlobalCategories to auto-populate when no sales budget categories exist
  const [defaultSalesCategories, setDefaultSalesCategories] = useState<SalesCategory[]>([]);

  useEffect(() => {
    if (open && (!budgetData.salesBudget?.categories || budgetData.salesBudget.categories.length === 0)) {
      fetchDefaultCategoriesAsSalesCategories()
        .then(categories => {
          // Pre-populate Labour category budget from resource budget cascade values
          const roles = budgetData.resourceBudget?.byRole ?? [];
          if (roles.length > 0) {
            const labourTotal = roles.reduce(
              (sum, role) => sum + (role.budgetHours * role.billRate),
              0
            );
            if (labourTotal > 0) {
              categories = categories.map(cat =>
                cat.type === 'labour'
                  ? { ...cat, budget: labourTotal, remaining: labourTotal }
                  : cat
              );
            }
          }

          setDefaultSalesCategories(categories);
        })
        .catch(err => {
          console.warn('[EditBudgetModal] Failed to load default sales GlobalCategories:', err);
        });
    }
  }, [open, budgetData.salesBudget?.categories?.length]);

  // Reset activeTab when modal opens with a new initialTab
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  // Determine which tabs should be visible based on the context
  const visibleTabs = (() => {
    switch (initialTab) {
      case 'cost':
        return ['cost', 'revision'];
      case 'sales':
        return ['sales']; // Removed revision tab - will come back to revisions later
      case 'resource':
        return ['resource', 'revision'];
      default:
        return ['cost', 'sales', 'resource', 'revision'];
    }
  })();

  // Calculate total change percentage
  const calculateChangePercentage = () => {
    if (!costBudgetChanges) return 0;
    
    const originalTotal = budgetData.costBudget?.totals?.budget || 0;
    const newTotal = costBudgetChanges.totals?.budget || originalTotal;
    
    if (originalTotal === 0) return 0;
    
    return Math.abs(((newTotal - originalTotal) / originalTotal) * 100);
  };

  const changePercentage = calculateChangePercentage();
  const requiresApproval = changePercentage > 5;

  const handleSave = async (asDraft: boolean = false) => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate revision reason if required
      if (!asDraft && requiresApproval && !revisionReason.trim()) {
        setError('Please provide a reason for this budget change (required for changes >5%)');
        return;
      }

      // Validate sales budget for Fixed Price projects
      if (salesBudgetChanges && projectType === 'fixed-price') {
        const contractValue = salesBudgetChanges.contractValue ?? budgetData.salesBudget?.contractValue ?? 0;
        if (contractValue <= 0) {
          setError('Contract value must be greater than zero for Fixed Price projects');
          return;
        }

        // 15-FE.2: Block save if any two milestones share the same triggerPercent
        if ((salesBudgetChanges as any)._hasDuplicateTrigger) {
          setError('Two or more milestones share the same trigger %. Each milestone must have a unique trigger threshold before saving.');
          return;
        }

        // Block save if contract value does not match the payment schedule total
        if ((salesBudgetChanges as any)._hasContractMismatch) {
          setError('Contract Value doesn\'t match Payment Schedule Total. Please ensure the values match before saving.');
          return;
        }

        // Validate payment schedule items have name and (for date projects) a due date
        const milestones = salesBudgetChanges.milestones ?? budgetData.salesBudget?.milestones ?? [];
        if (milestones.length > 0) {
          const invalidMilestones = milestones.filter((m: any) => {
            if (!m.name?.trim()) return true;
            if (billingTriggerType === 'date' && !m.dueDate) return true;
            return false;
          });
          if (invalidMilestones.length > 0) {
            setError(
              billingTriggerType === 'percent-complete'
                ? 'All payment schedule items must have a name'
                : 'All payment schedule items must have a name and due date'
            );
            return;
          }
        }
      }

      // Combine all changes
      const updatedBudget: Partial<BudgetData> = {};
      
      if (costBudgetChanges) {
        updatedBudget.costBudget = costBudgetChanges;
      }
      
      if (salesBudgetChanges) {
        updatedBudget.salesBudget = salesBudgetChanges;
      }
      
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      await onSave(updatedBudget, revisionReason, saveAsBaseline);
      
      // Reset state
      setIsDirty(false);
      setCostBudgetChanges(null);
      setSalesBudgetChanges(null);
      setResourceBudgetChanges(null);
      setRevisionReason('');
      setSaveAsBaseline(false);
      
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelDialog(true);
      return;
    }
    closeModal();
  };

  const closeModal = () => {
    // Reset state
    setIsDirty(false);
    setCostBudgetChanges(null);
    setSalesBudgetChanges(null);
    setResourceBudgetChanges(null);
    setRevisionReason('');
    setSaveAsBaseline(false);
    setError(null);
    setShowCancelDialog(false);
    onOpenChange(false);
  };

  const handleConfirmCancel = () => {
    closeModal();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // User is trying to close the modal
      handleCancel();
    } else {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Edit Sales Budget</DialogTitle>
          <DialogDescription>
            Update budget values for {budgetData.projectName}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Scrollable body — takes remaining height, lets inner horizontal scroll work */}
        <div className="flex-1 overflow-y-auto min-h-0">

        {/* Only show tabs if there's more than one visible tab */}
        {visibleTabs.length > 1 ? (
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'cost' | 'sales' | 'resource')} className="w-full">
            <TabsList className={`grid w-full grid-cols-${visibleTabs.length}`}>
              {visibleTabs.includes('cost') && (
                <TabsTrigger value="cost">Cost Budget</TabsTrigger>
              )}
              {visibleTabs.includes('sales') && (
                <TabsTrigger value="sales">Sales Budget</TabsTrigger>
              )}
              {visibleTabs.includes('resource') && (
                <TabsTrigger value="resource">Resource Budget</TabsTrigger>
              )}
              {visibleTabs.includes('revision') && (
                <TabsTrigger value="revision">Revision Details</TabsTrigger>
              )}
            </TabsList>

            {visibleTabs.includes('cost') && (
              <TabsContent value="cost" className="space-y-4">
                <EditCostBudgetForm
                  costBudget={budgetData.costBudget}
                  onChange={(changes) => {
                    setCostBudgetChanges(changes);
                    setIsDirty(true);
                  }}
                    availableCategories={budgetData.costBudget?.categories?.length > 0 
                      ? budgetData.costBudget.categories 
                      : defaultGlobalCategories}
                  resourceBudget={budgetData.resourceBudget}
                />
              </TabsContent>
            )}

            {visibleTabs.includes('sales') && (
              <TabsContent value="sales" className="space-y-4">
                {projectType === 'time-and-materials' ? (
                  <>
                    <EditSalesBudgetFormTM
                      projectId={budgetData.projectId}
                      salesBudget={budgetData.salesBudget}
                      onChange={(changes) => {
                        setSalesBudgetChanges(changes);
                        setIsDirty(true);
                      }}
                      projectStartDate={projectStartDate}
                      projectEndDate={projectEndDate}
                      availableCategories={budgetData.salesBudget?.categories?.length
                        ? budgetData.salesBudget.categories
                        : defaultSalesCategories}
                      resourceBudget={budgetData.resourceBudget}
                      hasResourceBudget={(budgetData.resourceBudget?.byRole?.length ?? 0) > 0}
                    />
                  </>
                ) : (
                  <EditSalesBudgetForm
                    projectId={budgetData.projectId}
                    salesBudget={budgetData.salesBudget}
                    projectType={projectType}
                    billingTriggerType={billingTriggerType}
                    onChange={(changes) => {
                      setSalesBudgetChanges(changes);
                      setIsDirty(true);
                    }}
                  />
                )}
              </TabsContent>
            )}

            {visibleTabs.includes('resource') && (
              <TabsContent value="resource" className="space-y-4">
                <EditResourceBudgetForm
                  projectId={budgetData.projectId}
                  resourceBudget={budgetData.resourceBudget}
                  onChange={(changes) => {
                    setResourceBudgetChanges(changes);
                    setIsDirty(true);
                  }}
                />
              </TabsContent>
            )}

            {visibleTabs.includes('revision') && (
              <TabsContent value="revision" className="space-y-4">
                <BudgetRevisionPanel
                  changePercentage={changePercentage}
                  requiresApproval={requiresApproval}
                  reasonCategory={reasonCategory}
                  onReasonCategoryChange={setReasonCategory}
                  revisionReason={revisionReason}
                  onRevisionReasonChange={setRevisionReason}
                  saveAsBaseline={saveAsBaseline}
                  onSaveAsBaselineChange={setSaveAsBaseline}
                  baselineName={baselineName}
                  onBaselineNameChange={setBaselineName}
                  costBudgetChanges={costBudgetChanges}
                  salesBudgetChanges={salesBudgetChanges}
                  resourceBudgetChanges={resourceBudgetChanges}
                  originalBudget={budgetData}
                />
              </TabsContent>
            )}
          </Tabs>
        ) : (
          /* Single tab - render content directly without tabs UI */
          <div className="space-y-4">
            {visibleTabs.includes('cost') && (
              <EditCostBudgetForm
                costBudget={budgetData.costBudget}
                onChange={(changes) => {
                  setCostBudgetChanges(changes);
                  setIsDirty(true);
                }}
                availableCategories={budgetData.costBudget?.categories?.length > 0 
                  ? budgetData.costBudget.categories 
                  : defaultGlobalCategories}
                resourceBudget={budgetData.resourceBudget}
              />
            )}

            {visibleTabs.includes('sales') && (
              <>
                {projectType === 'time-and-materials' ? (
                  <>
                    <EditSalesBudgetFormTM
                      projectId={budgetData.projectId}
                      salesBudget={budgetData.salesBudget}
                      onChange={(changes) => {
                        setSalesBudgetChanges(changes);
                        setIsDirty(true);
                      }}
                      projectStartDate={projectStartDate}
                      projectEndDate={projectEndDate}
                      availableCategories={budgetData.salesBudget?.categories?.length
                        ? budgetData.salesBudget.categories
                        : defaultSalesCategories}
                      resourceBudget={budgetData.resourceBudget}
                      hasResourceBudget={(budgetData.resourceBudget?.byRole?.length ?? 0) > 0}
                    />
                  </>
                ) : (
                  <EditSalesBudgetForm
                    projectId={budgetData.projectId}
                    salesBudget={budgetData.salesBudget}
                    projectType={projectType}
                    billingTriggerType={billingTriggerType}
                    onChange={(changes) => {
                      setSalesBudgetChanges(changes);
                      setIsDirty(true);
                    }}
                  />
                )}
              </>
            )}

            {visibleTabs.includes('resource') && (
              <EditResourceBudgetForm
                projectId={budgetData.projectId}
                resourceBudget={budgetData.resourceBudget}
                onChange={(changes) => {
                  setResourceBudgetChanges(changes);
                  setIsDirty(true);
                }}
              />
            )}
          </div>
        )}

        </div>{/* end scrollable body */}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {isDirty && (
              <span className="text-warning-600">
                • Unsaved changes ({changePercentage.toFixed(1)}% change)
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              onClick={() => handleSave(false)}
              disabled={!isDirty || isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              {requiresApproval ? 'Submit for Approval' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
